import pointsTranslation from '@r/translations/points-translation.ts';
import {
  CoinsIcon,
  PercentCircleIcon,
  FactoryIcon,
  ShoppingCartIcon,
  WalletIcon
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DashboardStatsSection,
  DashboardStatTile,
  DashboardStatTileSkeleton
} from '@admin/components/dashboard/dashboard-stats-section.tsx';
import { CompanyRankingList } from '@admin/components/company/company-ranking-list.tsx';
import { useApiAdmininsightsdashboardGetCollection } from '@r/api/generated/client.ts';
import type { InsightDashboardInsightDashboardReadModel } from '@r/api/generated/client.schemas.ts';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { ProgramExpirationBanner } from '@admin/components/renew-program/program-expiration-banner.tsx';
import { WorkspaceMisconfiguredBanner } from '@admin/components/workspace-configuration/workspace-misconfigured-banner.tsx';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type AdminInsights = InsightDashboardInsightDashboardReadModel;

const STATS_SKELETON_LAYOUT = [
  { gridClassName: '', tileClassNames: ['', ''] },
  {
    gridClassName: '2xl:grid-cols-6',
    tileClassNames: [
      '2xl:col-span-2',
      '2xl:col-span-2',
      'sm:col-span-2',
      '2xl:col-span-3',
      '2xl:col-span-3'
    ]
  }
];

export const AdminDashboard = () => {
  const { t } = useTranslation();

  const user = useUser().data;
  const workspaces = UseWorkspace(user);
  const logicWorkspace = workspaces.workspace?.currentLogicWorkspace;
  const canFetchDashboard = Boolean(logicWorkspace);

  const adminInsightsQuery = useApiAdmininsightsdashboardGetCollection({
    query: { enabled: canFetchDashboard }
  });
  useOrvalQueryErrorToast(adminInsightsQuery, t('toast.error.label'));

  let registeredCompanyPercentage: number | null = null;
  let totalAcquiredPoints: number | null = null;
  let totalSpentPoints: number | null = null;
  let totalBalance: number | null = null;
  let spentOverAcquiredPercent: number | null = null;
  let registeredCompaniesPointsPercent: number | null = null;

  if (adminInsightsQuery.isSuccess) {
    registeredCompanyPercentage = getRegisteredCompaniesPercentage(adminInsightsQuery.data);
    totalAcquiredPoints = adminInsightsQuery.data.insightPoint.totalAcquiredPoints ?? null;
    totalSpentPoints =
      adminInsightsQuery.data.insightPoint.totalSpentPoints !== null
        ? Math.abs(adminInsightsQuery.data.insightPoint.totalSpentPoints)
        : null;
    totalBalance = adminInsightsQuery.data.insightPoint.totalBalance ?? null;
    if (totalAcquiredPoints !== null && totalAcquiredPoints > 0 && totalSpentPoints !== null) {
      spentOverAcquiredPercent = (totalSpentPoints / totalAcquiredPoints) * 100;
    }
    if (
      totalAcquiredPoints !== null &&
      totalAcquiredPoints > 0 &&
      adminInsightsQuery.data.insightPoint.totalAcquiredPointsRegisteredCompanies !== null
    ) {
      registeredCompaniesPointsPercent =
        (adminInsightsQuery.data.insightPoint.totalAcquiredPointsRegisteredCompanies /
          totalAcquiredPoints) *
        100;
    }
  }

  function getRegisteredCompaniesPercentage(adminInsights: AdminInsights): number {
    if (
      adminInsights.insightCompany.totalCompanies === 0 ||
      adminInsights.insightCompany.totalCompaniesRegistered === 0
    ) {
      return 0;
    }

    return Math.round(
      (adminInsights.insightCompany.totalCompaniesRegistered /
        adminInsights.insightCompany.totalCompanies) *
        100
    );
  }

  return (
    <div className="flex flex-col space-y-5 grow">
      <div className="flex flex-col gap-3 empty:hidden">
        <ProgramExpirationBanner />
        <WorkspaceMisconfiguredBanner />
      </div>
      <h1 className="text-4xl font-bold">{t('admin.dashboard.stats.title')}</h1>
      <div className="flex flex-wrap space-y-5 xl:flex-nowrap xl:space-x-5 xl:space-y-0">
        <div className="flex flex-col space-y-6 w-full xl:w-3/5 xl:self-start">
          {(adminInsightsQuery.isLoading || adminInsightsQuery.isError) &&
            STATS_SKELETON_LAYOUT.map(({ gridClassName, tileClassNames }, sectionIndex) => (
              <div
                key={`stats-section-skeleton-${sectionIndex}`}
                className="flex flex-col p-4 space-y-3 rounded-3xl shadow-sm border border-foreground/10"
              >
                <div className="min-h-8" />
                <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${gridClassName}`}>
                  {tileClassNames.map((tileClassName, tileIndex) => (
                    <DashboardStatTileSkeleton
                      key={`stats-tile-skeleton-${tileIndex}`}
                      className={tileClassName}
                    />
                  ))}
                </div>
              </div>
            ))}

          {adminInsightsQuery.isSuccess && adminInsightsQuery.data && (
            <>
              <DashboardStatsSection
                title={t('admin.dashboard.stats.section.companies')}
                link={{ to: '/companies/insights', label: t('admin.dashboard.stats.see.more') }}
              >
                {adminInsightsQuery.data.insightCompany.totalCompaniesRegistered !== null &&
                  adminInsightsQuery.data.insightCompany.totalCompanies !== null && (
                    <DashboardStatTile
                      title={t('admin.kpi.registered.companies.number')}
                      icon={<FactoryIcon size={16} />}
                      value={adminInsightsQuery.data.insightCompany.totalCompaniesRegistered}
                      valueSuffix={` / ${adminInsightsQuery.data.insightCompany.totalCompanies.toLocaleString()}`}
                    />
                  )}

                {registeredCompanyPercentage !== null && (
                  <DashboardStatTile
                    title={t('admin.kpi.registered.companies.percent')}
                    icon={<PercentCircleIcon size={16} />}
                    value={registeredCompanyPercentage}
                    valueSuffix=" %"
                  />
                )}
              </DashboardStatsSection>

              <DashboardStatsSection
                title={t('admin.dashboard.stats.section.points')}
                link={{ to: '/points/insights', label: t('admin.dashboard.stats.see.more') }}
                className="2xl:grid-cols-6"
              >
                {totalAcquiredPoints !== null && (
                  <DashboardStatTile
                    title={t('admin.kpi.points.cumulated')}
                    icon={<CoinsIcon size={16} />}
                    value={totalAcquiredPoints}
                    className="2xl:col-span-2"
                    valueSuffix={` ${pointsTranslation(t, totalAcquiredPoints)}`}
                  />
                )}

                {totalSpentPoints !== null && (
                  <DashboardStatTile
                    title={t('admin.kpi.points.spent')}
                    icon={<ShoppingCartIcon size={16} />}
                    value={totalSpentPoints}
                    className="2xl:col-span-2"
                    valueSuffix={` ${pointsTranslation(t, totalSpentPoints)}`}
                  />
                )}

                {totalBalance !== null && (
                  <DashboardStatTile
                    title={t('admin.kpi.points.balance')}
                    icon={<WalletIcon size={16} />}
                    value={totalBalance}
                    className="sm:col-span-2"
                    valueSuffix={` ${pointsTranslation(t, totalBalance)}`}
                  />
                )}

                {registeredCompaniesPointsPercent !== null && (
                  <DashboardStatTile
                    title={t('admin.kpi.points.registered.companies.percent')}
                    icon={<PercentCircleIcon size={16} />}
                    value={registeredCompaniesPointsPercent}
                    valueSuffix=" %"
                    decimals={2}
                    className="2xl:col-span-3"
                  />
                )}

                {spentOverAcquiredPercent !== null && (
                  <DashboardStatTile
                    title={t('admin.kpi.points.spent.percent')}
                    icon={<PercentCircleIcon size={16} />}
                    value={spentOverAcquiredPercent}
                    valueSuffix=" %"
                    decimals={2}
                    className="2xl:col-span-3"
                  />
                )}
              </DashboardStatsSection>
            </>
          )}
        </div>

        <div className="w-full xl:w-2/5 xl:min-h-full">
          <CompanyRankingList
            className="xl:h-full"
            orderBy="acquiredPoints"
            direction="desc"
            rankingValueType={'point'}
            rankingItems={adminInsightsQuery.data?.insightCompany.ranking}
            rankingLoading={adminInsightsQuery.isLoading}
            rankingError={adminInsightsQuery.isError}
            disableInternalFetch={true}
          />
        </div>
      </div>
    </div>
  );
};
